import Text from "common/Typography/Text";
import {
  LanguageNameSpaces,
  LoginRequest,
  OAuthUserRequest,
} from "@shared/types";
import { AuthDescription, PrimaryTitle, Wrapper } from "features/auth/styles";
import AuthInlineError, {
  useAuthResultMessage,
} from "features/auth/AuthInlineError";
import { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import React from "react";
import { Security } from "common/Icons/Icons";
import VerificationCode from "features/auth/VerificationCode";
import { sharedLib } from "@shared/services/shared.service";
import { handleUserTempOAuthData } from "@shared/features/onboarding/utils";
import { triggerSaveBiometric } from "services/webview/webViewBridgeUtils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface NewIpLoginProps {
  loginDetails: LoginRequest | OAuthUserRequest;
  isBiometricLogin?: boolean;
  onFinalizedLogin: () => void;
}

const NewIpLogin = ({
  loginDetails,
  isBiometricLogin,
  onFinalizedLogin,
}: NewIpLoginProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [login, loginStatus] =
    sharedLib.sharedApi.authentication.useLoginMutation();
  const [oauthUser, oauthUserStatus] =
    sharedLib.sharedApi.authentication.useOauthUserMutation();
  const isLoading = loginStatus.isLoading || oauthUserStatus.isLoading;
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { isNewLook } = useTheme();
  const { display: displayResultMessage, error } = useAuthResultMessage();

  const onSubmit = (code: string) => {
    const loginRequest = { ...loginDetails, new_ip_token: code };
    if ("email" in loginDetails) {
      displayResultMessage(login(loginRequest as LoginRequest).unwrap())
        .then(() => {
          onFinalizedLogin();
          if (!isBiometricLogin) {
            return triggerSaveBiometric(loginDetails);
          }
        })
        .catch(() => {});
    } else {
      displayResultMessage(oauthUser(loginRequest as OAuthUserRequest).unwrap())
        .then(async (result) => {
          onFinalizedLogin();
          if (result?.user_id) {
            handleUserTempOAuthData(result.user_id);
          }
        })
        .catch(() => {});
    }
  };

  return (
    <Wrapper $isMobile={isMobile}>
      {isNewLook ? (
        <>
          <PrimaryTitle $isMobile={isMobile}>
            {t("new_ip.title_new_look")}
          </PrimaryTitle>
          <AuthDescription>{t("new_ip.description")}</AuthDescription>
        </>
      ) : (
        <>
          <PrimaryTitle $isMobile={isMobile}>{t("new_ip.title")}</PrimaryTitle>
          <Security style={{ marginBottom: 24 }} />
          <Text>{t("new_ip.description")}</Text>
        </>
      )}
      <VerificationCode
        numOfDigits={NUM_OF_DIGITS_CODE}
        title={t(
          isNewLook
            ? "verify_email.verification_code_new_look"
            : "verify_email.verification_code"
        )}
        onSubmit={onSubmit}
        isLoading={isLoading}
        additionalItems={
          error ? (
            <AuthInlineError message={error} style={{ margin: "16px 0 0" }} />
          ) : undefined
        }
      />
    </Wrapper>
  );
};

export default NewIpLogin;

const NUM_OF_DIGITS_CODE = 6;
